import React, { forwardRef, useId } from 'react';
import PropTypes from 'prop-types';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { FIELD_SIZES } from '../../constants';
import { buildInputClasses, FieldMessage } from '../Input/fieldHelpers';

const Select = forwardRef(({
  value,
  onChange,
  disabled = false,
  error = false,
  size = 'md',
  className = '',
  children,
  helperText,
  errorMessage,
  id: idProp,
  ...props
}, ref) => {
  const generatedId = useId();
  const inputId = idProp || generatedId;
  const messageId = `${inputId}-message`;
  const hasMessage = Boolean(helperText || errorMessage);
  const hasError = error || Boolean(errorMessage);

  const selectElement = (
    <div className="relative w-full">
      <select
        ref={ref}
        id={inputId}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-invalid={hasError || undefined}
        aria-describedby={hasMessage ? messageId : undefined}
        className={buildInputClasses({
          size,
          error: hasError,
          className,
          extra: ['select-control']
        })}
        {...props}
      >
        {children}
      </select>
      <ChevronDownIcon
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
        aria-hidden="true"
      />
    </div>
  );

  if (!hasMessage) return selectElement;

  return (
    <div className="w-full">
      {selectElement}
      <FieldMessage id={messageId} helperText={helperText} errorMessage={errorMessage} />
    </div>
  );
});

Select.displayName = 'Select';

Select.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  size: PropTypes.oneOf(FIELD_SIZES),
  className: PropTypes.string,
  children: PropTypes.node,
  helperText: PropTypes.string,
  errorMessage: PropTypes.string,
  id: PropTypes.string
};

export default Select;
